import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageWithFallback } from "@/components/ui/image-with-fallback";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { MAX_FOTO_LAUDO_BYTES, MAX_OBSERVACAO_PARCEIRO } from "@shared/laudo-midia";

interface Props {
  peca: { id: string; nome: string; fotoUrl: string | null; observacaoParceiro: string | null };
  podeEditar: boolean;
  /** Owner: ocultar o campo legado no fluxo do Parceiro, mesmo quando não há perguntas para uma peça. */
  ocultarObservacao?: boolean;
  onSaved: () => Promise<unknown>;
  onAmpliar: (url: string) => void;
}

export function FotoObservacaoPeca({ peca, podeEditar, ocultarObservacao = false, onSaved, onAmpliar }: Props) {
  const { session } = useAuth();
  const { toast } = useToast();
  const arquivoRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ file: File; url: string } | null>(null);
  const [observacao, setObservacao] = useState(peca.observacaoParceiro ?? "");
  const [salvandoFoto, setSalvandoFoto] = useState(false);
  const [salvandoObservacao, setSalvandoObservacao] = useState(false);
  const [erroFoto, setErroFoto] = useState<string | null>(null);
  const [erroObservacao, setErroObservacao] = useState<string | null>(null);
  const mobile = typeof navigator !== "undefined" &&
    (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

  useEffect(() => { setObservacao(peca.observacaoParceiro ?? ""); }, [peca.observacaoParceiro]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  async function enviar(url: string, method: string, body: BodyInit, contentType: string) {
    if (!session?.access_token) throw new Error("Sua sessão expirou. Entre novamente para salvar.");
    const res = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": contentType },
      body,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) await onSaved();
      throw new Error(data.message || (res.status === 413 ? "A foto deve ter até 10 MB." : "Não foi possível salvar. Tente novamente."));
    }
  }

  function selecionar(file?: File) {
    if (!file) return;
    if (file.size === 0 || file.size > MAX_FOTO_LAUDO_BYTES || !["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast({ title: "Foto inválida", description: "Escolha uma foto JPG, PNG ou WebP com até 10 MB.", variant: "destructive" });
      return;
    }
    setErroFoto(null);
    setPreview({ file, url: URL.createObjectURL(file) });
  }

  async function confirmarFoto() {
    if (!preview || !podeEditar || salvandoFoto) return;
    setSalvandoFoto(true);
    setErroFoto(null);
    try {
      await enviar(`/api/laudo-pecas/${peca.id}/foto`, "POST", preview.file, preview.file.type);
      setPreview(null);
      await onSaved();
      toast({ title: "Foto salva" });
    } catch (error) { setErroFoto(error instanceof Error ? error.message : "Erro ao enviar foto. Tente novamente."); }
    finally { setSalvandoFoto(false); }
  }

  async function salvarObservacao() {
    if (!podeEditar || salvandoObservacao) return;
    setSalvandoObservacao(true);
    setErroObservacao(null);
    try {
      await enviar(`/api/laudo-pecas/${peca.id}/observacao`, "PUT", JSON.stringify({ observacaoParceiro: observacao.trim() || null }), "application/json");
      await onSaved();
      toast({ title: "Observação salva" });
    } catch (error) { setErroObservacao(error instanceof Error ? error.message : "Erro ao salvar observação. Tente novamente."); }
    finally { setSalvandoObservacao(false); }
  }

  const alterada = observacao.trim() !== (peca.observacaoParceiro ?? "");
  return (
    <div className="mt-2 space-y-3">
      <button type="button" className="block w-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" disabled={!peca.fotoUrl} aria-label={`Ampliar foto de ${peca.nome}`} onClick={() => { if (peca.fotoUrl) onAmpliar(peca.fotoUrl); }}>
        <ImageWithFallback key={peca.fotoUrl} src={peca.fotoUrl} alt={peca.nome}
          containerClassName="w-full h-40 rounded-md" className="w-full h-40 object-cover rounded-md" />
      </button>
      {podeEditar && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => arquivoRef.current?.click()} disabled={salvandoFoto}>
            <ImagePlus className="mr-2 h-4 w-4" />{peca.fotoUrl ? "Trocar foto" : "Adicionar foto"}
          </Button>
          {mobile && <Button type="button" variant="outline" size="sm" onClick={() => cameraRef.current?.click()} disabled={salvandoFoto}>
            <Camera className="mr-2 h-4 w-4" />Tirar foto
          </Button>}
          <input ref={arquivoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" aria-label={`Selecionar foto de ${peca.nome}`} onChange={(e) => { selecionar(e.target.files?.[0]); e.target.value = ""; }} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" aria-label={`Tirar foto de ${peca.nome}`} onChange={(e) => { selecionar(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      )}
      {!ocultarObservacao && (
        <div className="space-y-2">
          <Label htmlFor={`observacao-${peca.id}`}>Observações do Parceiro de Negócios{podeEditar ? " (opcional)" : ""}</Label>
          {podeEditar ? <>
            <Textarea id={`observacao-${peca.id}`} value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={MAX_OBSERVACAO_PARCEIRO} disabled={salvandoObservacao} placeholder="Descreva o estado da peça, problemas ou se está em bom estado." className="min-h-20" aria-describedby={`observacao-ajuda-${peca.id}`} />
            <div className="flex items-center justify-between gap-2">
              <span id={`observacao-ajuda-${peca.id}`} className="text-xs text-muted-foreground">{observacao.length}/{MAX_OBSERVACAO_PARCEIRO}</span>
              <Button type="button" size="sm" variant="outline" onClick={salvarObservacao} disabled={!alterada || salvandoObservacao}>
                {salvandoObservacao && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar observação
              </Button>
            </div>
          </> : <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">{peca.observacaoParceiro || "Nenhuma observação informada."}</p>}
          {erroObservacao && <p role="alert" className="text-sm text-destructive">{erroObservacao}</p>}
        </div>
      )}
      <Dialog open={!!preview} onOpenChange={(open) => { if (!open && !salvandoFoto) setPreview(null); }}>
        <DialogContent className="max-w-xl max-h-[90dvh] overflow-y-auto" onEscapeKeyDown={(e) => { if (salvandoFoto) e.preventDefault(); }} onPointerDownOutside={(e) => { if (salvandoFoto) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle>Confirmar foto</DialogTitle>
            <DialogDescription>Confira a foto de {peca.nome} antes de salvar.{peca.fotoUrl ? " A foto atual será substituída." : ""}</DialogDescription>
          </DialogHeader>
          {preview && <img src={preview.url} alt={`Nova foto de ${peca.nome}`} className="w-full max-h-[55dvh] object-contain rounded-md" />}
          {!podeEditar && <p role="alert" className="text-sm text-destructive">O laudo saiu de análise. Não é possível trocar a foto.</p>}
          {erroFoto && <p role="alert" className="text-sm text-destructive">{erroFoto}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={salvandoFoto} onClick={() => setPreview(null)}>Cancelar</Button>
            <Button type="button" disabled={salvandoFoto || !podeEditar} onClick={confirmarFoto}>
              {salvandoFoto && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{salvandoFoto ? "Salvando…" : "Confirmar foto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
